import {action, computed, makeObservable, observable, override, runInAction} from "mobx";

import {TemplateDetailOutput} from "../../../api/values/database-detail-output";
import {
    TemplateColumnListItemInput,
    TemplateUpsertInput,
} from "../../../api/values/database-upsert-input";
import {Permission} from "../../../stores/authentication-store";
import {
    DatabaseFormBaseStore,
    // STRING_TYPE,
    TableRow,
} from "../../../stores/database-template/database-form-base";
import {isPersistedId} from "../../../utils/is-presisted-id";
import {PageStoreConstructorOptions} from "../../../utils/page-store";
import {ColumnDef} from "../../atoms/tables/table";

export class DatabaseTemplateDetailEditPageStore extends DatabaseFormBaseStore {
    /**
     * Observables
     */
    @observable public templateId = "";
    @observable public templateDetail: TemplateDetailOutput = new TemplateDetailOutput();
    @observable private _originalTableRows: TableRow[] = [];
    @observable private _originalTemplateName = "";
    @observable private _originalTemplateNote = "";

    /**
     * テーブルヘッダーのデフォルト値
     * @type {ColumnDef[]}
     */
    public static readonly DEFAULT_COLUMN_DEFS: ColumnDef[] = [
        {label: "システムID", width: 14},
        {label: "項目ID", width: 187},
        {label: "項目名", width: 187},
        {label: "データの種類", width: 112},
        {label: "説明", width: 460, required: false},
        {label: "", width: 94, required: false},
    ];

    /**
     * テーブルヘッダーのデフォルト値を取得
     * @returns {ColumnDef[]} デフォルトの定義
     */
    public get DEFAULT_COLUMN_DEFS(): ColumnDef[] {
        return DatabaseTemplateDetailEditPageStore.DEFAULT_COLUMN_DEFS;
    }

    /**
     * Constructor
     */
    constructor(options: PageStoreConstructorOptions) {
        super(options);
        // デコレーターを有効化する
        makeObservable(this);
    }
    // 1a83637f-d26f-4235-b591-5a406cc0071b 58
    /**
     *  初期ロード
     */
    public async initialize({
        query,
    }: {
        query: {mediaId: string; templateId: string};
    }): Promise<void> {
        this.stores.authenticationStore.checkAnyPermissionExists(
            Permission.MANAGE_SYSTEM,
            Permission.MANAGE_MEDIA,
        );
        this.setMediaId(query.mediaId || "");
        this.setTemplateId(query.templateId || "");
        await this.load();
    }

    /** テンプレートIDをセット */
    @action
    public setTemplateId(templateId: string) {
        this.templateId = templateId;
    }

    /**
     * テンプレートの行データを追加
     * - 新規テンプレートを追加する
     */
    @action.bound
    public async load(): Promise<void> {
        try {
            const res = await this.adminApi.getTemplate(
                {mediaId: this.mediaId},
                {templateId: this.templateId},
            );
            runInAction(() => {
                this.templateName = res.title;
                this.templateNote = res.note;
                // templateColumnListをフィルタリングして、deletedフラグがfalseのものだけを取得
                this.templateColumnList = res.templateColumnList
                    .filter((c) => !c.deleted)
                    .map((c) => ({
                        id: c.id,
                        slug: c.slug,
                        slugError: "",
                        label: c.label,
                        labelError: "",
                        type: c.type,
                        note: c.note,
                        noteError: "",
                        deleted: c.deleted,
                    }));
                // オリジナル値を保持
                this._originalTemplateName = this.templateName;
                this._originalTemplateNote = this.templateNote;
                this._originalTableRows = this.templateColumnList.map((r) => ({...r}));
            });
        } catch (error) {
            throw new Error("Error loading template: " + error);
        }
    }

    /**
     * 編集時の削除ロジック
     * - 既存行（isPersistedId===true）の場合 → deletedフラグをtrueに
     * - 新規行（isPersistedId===false）の場合 → 親クラス(super)の物理削除を実行
     * @param id 削除対象の行ID
     */
    @override
    public removeTemplateRow(id: string) {
        const row = this.templateColumnList.find((r) => r.id === id);
        if (!row) return;

        if (isPersistedId(id)) {
            // 既存データ は論理削除フラグのみ切り替え
            row.deleted = true;
        } else {
            // 新規追加データ は配列から完全に削除
            super.removeTemplateRow(id);
        }
    }

    /**
     * テンプレートの更新処理
     * - 更新後、詳細ページに遷移
     */
    @action.bound
    public async updateTemplate(): Promise<void> {
        const dto = new TemplateUpsertInput({
            title: this.templateName,
            note: this.templateNote,
            templateColumnList: this.templateColumnList.map(
                (r) =>
                    new TemplateColumnListItemInput({
                        id: isPersistedId(r.id) ? r.id : undefined, // 新規行はIDを設定しない
                        slug: r.slug,
                        label: r.label,
                        type: r.type,
                        note: r.note,
                        deleted: r.deleted,
                    }),
            ),
        });

        try {
            // APIに更新リクエストを送信
            await this.adminApi.putTemplate(
                {mediaId: this.mediaId},
                {templateId: this.templateId},
                dto,
            );
            runInAction(() => {
                // 保存完了後は詳細画面へリダイレクト
                this.pageRouter.pushRoute(
                    `/admin/${this.mediaId}/database-templates/detail/${this.templateId}`,
                );
            });
        } catch (error) {
            console.error("Error updating template:", error);
        }
    }

    /**
     * 更新ボタンの有効可否判定
     * - 親クラスのバリデーションOK && オリジナルと差分がある場合に有効
     */
    @computed
    public get canUpdate(): boolean {
        if (!this.canSave()) return false;

        if (this._originalTemplateName) {
            const nameUnchanged = this.templateName === this._originalTemplateName;
            const noteUnchanged = this.templateNote === this._originalTemplateNote;

            const rowsUnchanged =
                this.templateColumnList.length === this._originalTableRows.length &&
                this.templateColumnList.every((r, i) => {
                    const o = this._originalTableRows[i];
                    return (
                        r.id === o.id &&
                        r.slug === o.slug &&
                        r.label === o.label &&
                        r.type === o.type &&
                        r.note === o.note &&
                        r.deleted === o.deleted
                    );
                });

            // 全てオリジナルと同じなら更新不可
            if (nameUnchanged && noteUnchanged && rowsUnchanged) {
                return false;
            }
        }
        return true;
    }
}
